import { getApps, initializeApp, type FirebaseApp } from 'firebase/app'
import { getAuth } from 'firebase/auth'
import {
  getFirestore,
  initializeFirestore,
  memoryLocalCache,
  persistentLocalCache,
  persistentMultipleTabManager,
  type Firestore,
} from 'firebase/firestore'
import { isSharedDevicePath } from '../utils/sharedDeviceRoute'
import {
  shouldStartWithMemoryCacheDueToOfflineExpiry,
  enforceOfflineExpiry,
} from './offlineExpiry'
import { getDatabase } from 'firebase/database'
import { getStorage } from 'firebase/storage'
import { getFunctions } from 'firebase/functions'
import { initializeAppCheck, ReCaptchaEnterpriseProvider, type AppCheck } from 'firebase/app-check'

const requireEnv = (key: string) => {
  const value = import.meta.env[key] as string | undefined
  if (!value) {
    throw new Error(`Missing ${key}. Add it to .env locally and GitHub Actions secrets for production builds.`)
  }
  return value
}

const optionalDatabaseUrl = (import.meta.env.VITE_FIREBASE_DATABASE_URL as string | undefined) ?? ''

export const firebaseConfig = {
  apiKey: requireEnv('VITE_FIREBASE_API_KEY'),
  authDomain: requireEnv('VITE_FIREBASE_AUTH_DOMAIN'),
  projectId: requireEnv('VITE_FIREBASE_PROJECT_ID'),
  storageBucket: requireEnv('VITE_FIREBASE_STORAGE_BUCKET'),
  messagingSenderId: requireEnv('VITE_FIREBASE_MESSAGING_SENDER_ID'),
  appId: requireEnv('VITE_FIREBASE_APP_ID'),
  ...(optionalDatabaseUrl ? { databaseURL: optionalDatabaseUrl } : {}),
}

export const firebaseApp = getApps().length > 0 ? getApps()[0] : initializeApp(firebaseConfig)
const appCheckSiteKey = import.meta.env.VITE_FIREBASE_APPCHECK_SITE_KEY as string | undefined
import { shouldEnableAppCheckDebugToken } from './appCheckGating'
export { shouldEnableAppCheckDebugToken }

export const isDebugMode = false

const initializedAppChecks = new WeakMap<FirebaseApp, AppCheck>()

export function initAppCheckFor(app: FirebaseApp): AppCheck | undefined {
  if (initializedAppChecks.has(app)) {
    return initializedAppChecks.get(app)
  }
  const siteKey = appCheckSiteKey || (import.meta.env.VITE_FIREBASE_APPCHECK_SITE_KEY as string | undefined)
  if (siteKey && typeof document !== 'undefined') {
    const isDebugAppCheck = shouldEnableAppCheckDebugToken(import.meta.env)

    if (import.meta.env.DEV && isDebugAppCheck) {
      const configuredDebugToken = (import.meta.env.VITE_FIREBASE_APPCHECK_DEBUG_TOKEN as string | undefined)?.trim()
      const debugTokenValue: string | boolean = configuredDebugToken || true
      if (typeof self !== 'undefined') {
        ;(self as typeof self & { FIREBASE_APPCHECK_DEBUG_TOKEN?: string | boolean }).FIREBASE_APPCHECK_DEBUG_TOKEN = debugTokenValue
      }
      ;(globalThis as typeof globalThis & { FIREBASE_APPCHECK_DEBUG_TOKEN?: string | boolean }).FIREBASE_APPCHECK_DEBUG_TOKEN = debugTokenValue
    }

    const instance = initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(siteKey),
      isTokenAutoRefreshEnabled: true,
    })
    initializedAppChecks.set(app, instance)
    return instance
  }
  return undefined
}

initAppCheckFor(firebaseApp)
export const firebaseAuth = getAuth(firebaseApp)

// Offline persistence — caches reads in IndexedDB and queues writes in a local
// mutation buffer that replays when the network returns. Benefits web + both
// Capacitor wrappers. On the shared-device route (/shared-device*), use
// memoryLocalCache instead so person B's listeners never read person A's cached
// documents from IndexedDB. initializeFirestore throws if called twice for the
// same app (hot reload), so fall back to the existing instance.
let firestoreInstance: Firestore
try {
  const isSharedDevice = isSharedDevicePath(
    typeof window !== 'undefined' ? window.location.pathname : '',
  )
  const isExpired =
    typeof window !== 'undefined' && shouldStartWithMemoryCacheDueToOfflineExpiry()
  if (isExpired) {
    void enforceOfflineExpiry()
  }
  firestoreInstance = initializeFirestore(firebaseApp, {
    localCache: isSharedDevice || isExpired
      ? memoryLocalCache()
      : persistentLocalCache({
          tabManager: persistentMultipleTabManager(),
        }),
  })
} catch {
  firestoreInstance = getFirestore(firebaseApp)
}
export const firestoreDb = firestoreInstance
export const firebaseRtdb = getDatabase(firebaseApp)
export const firebaseStorage = getStorage(firebaseApp)
export const REGION = 'asia-south1'
export const firebaseFunctions = getFunctions(firebaseApp, REGION)

type FirebaseCompat = {
  apps: unknown[]
  initializeApp: (config: typeof firebaseConfig) => unknown
  auth: {
    GoogleAuthProvider: new () => unknown
    Auth: { Persistence: { LOCAL: string } }
  } & ((app?: unknown) => {
    setPersistence: (persistence: string) => Promise<void>
    onAuthStateChanged: (listener: (user: unknown) => void) => () => void
    signInWithPopup: (provider: unknown) => Promise<unknown>
    signOut: () => Promise<void>
  })
  firestore: (app?: unknown) => FirestoreRoot
}

type FirestoreDoc = {
  collection: (path: string) => FirestoreCollection
  get: () => Promise<{ exists: boolean; data: () => unknown }>
  set: (data: unknown, options?: { merge: boolean }) => Promise<void>
}

type FirestoreCollection = {
  doc: (path: string) => FirestoreDoc
}

type FirestoreRoot = {
  collection: (path: string) => FirestoreCollection
}

declare global {
  interface Window {
    firebase?: FirebaseCompat
  }
}

const firebaseVersion = '10.14.1'
const scriptUrls = [
  `https://www.gstatic.com/firebasejs/${firebaseVersion}/firebase-app-compat.js`,
  `https://www.gstatic.com/firebasejs/${firebaseVersion}/firebase-auth-compat.js`,
  `https://www.gstatic.com/firebasejs/${firebaseVersion}/firebase-firestore-compat.js`,
]

let firebasePromise: Promise<FirebaseCompat> | null = null

const loadScript = (src: string) =>
  new Promise<void>((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`)
    if (existing) {
      resolve()
      return
    }

    const script = document.createElement('script')
    script.src = src
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error(`Failed to load ${src}`))
    document.head.appendChild(script)
  })

export const getFirebase = async () => {
  if (!firebasePromise) {
    firebasePromise = scriptUrls.reduce(
      (chain, src) => chain.then(() => loadScript(src)),
      Promise.resolve(),
    ).then(() => {
      if (!window.firebase) throw new Error('Firebase failed to initialize.')
      const app = window.firebase.apps.length > 0 ? window.firebase.apps[0] : window.firebase.initializeApp(firebaseConfig)
      void window.firebase.auth(app).setPersistence(window.firebase.auth.Auth.Persistence.LOCAL)
      return window.firebase
    })
  }

  return firebasePromise
}
